/**
 * RaiseConcernModal
 *
 * Lets a participant raise a process-fairness concern about a session. This is a
 * flag, not an appeal — pick a concern type, optionally explain, submit. It
 * reaches the facilitator / cycle owner with the loop kept open until resolved.
 */

import { useState } from 'react';
import { Flag, X } from 'lucide-react';
import {
  FAIRNESS_CONCERN_LABELS,
  type FairnessConcernType,
} from '../../../types/prioritization/fairnessFlag';

const ORDER: FairnessConcernType[] = [
  'vote_suppressed',
  'not_heard',
  'process_unfair',
  'criteria_biased',
  'other',
];

interface RaiseConcernModalProps {
  onSubmit: (concernType: FairnessConcernType, note: string) => Promise<void> | void;
  onClose: () => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function RaiseConcernModal({ onSubmit, onClose, isSubmitting = false, error }: RaiseConcernModalProps) {
  const [concernType, setConcernType] = useState<FairnessConcernType>('vote_suppressed');
  const [note, setNote] = useState('');

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md bg-[var(--bg-raised)] shadow-xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--mustard-dim)] flex items-center justify-center flex-shrink-0">
              <Flag className="h-4 w-4 text-[var(--mustard)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-pri)]">Raise a concern</h3>
              <p className="text-xs text-[var(--text-ter)]">
                Flag a problem with how this session is being run. It goes to the facilitator.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="space-y-1.5">
            {ORDER.map(type => (
              <label
                key={type}
                className={`flex items-center gap-2.5 px-3 py-2  border cursor-pointer text-sm transition-colors ${
                  concernType === type
                    ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--text-pri)]'
                    : 'border-[var(--rule)] hover:bg-[var(--surface)] text-[var(--text-sec)]'
                }`}
              >
                <input
                  type="radio"
                  name="concern-type"
                  value={type}
                  checked={concernType === type}
                  onChange={() => setConcernType(type)}
                  className="accent-amber-600"
                />
                {FAIRNESS_CONCERN_LABELS[type]}
              </label>
            ))}
          </div>

          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            placeholder="Add detail (optional) — what happened, and what you'd want done about it…"
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-amber-400 focus:border-[var(--mustard)] resize-none"
          />

          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)]">
          <button onClick={onClose} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            Cancel
          </button>
          <button
            onClick={() => void onSubmit(concernType, note)}
            disabled={isSubmitting}
            className="text-sm font-semibold px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting…' : 'Submit concern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RaiseConcernModal;
